import moment from 'moment';
import GPXBuilder, { IGPXBuilder } from "./gpxBuilder";

// OL
import OlFeature from "ol/Feature";
import OlPoint from "ol/geom/Point";
import OlLineString from "ol/geom/LineString";
import { Coordinate as OlCoordinate } from "ol/coordinate";
import { transform } from 'ol/proj';

// Types
import { trksegType, trkType, wptType, metadataType, personType, linkType } from "./gpxModel";
import { DCRecord } from "@/@types/lib/dataController";
import { RoutePoint } from '@/components/MapRouting/useRouting';

export interface IGPXBuilderOL {
  addStaza: (rec: DCRecord, feat: OlFeature<OlLineString>) => void;
  addStazaFromRoute: (rec: DCRecord, feat: OlFeature<OlLineString>, routingPoints: Array<RoutePoint>) => void;
  writeGPX: () => string;
}

class GPXBuilderOL implements IGPXBuilderOL {

  gpxBuilder: IGPXBuilder;

  constructor() {
    this.gpxBuilder = new GPXBuilder('SigurneStaze.hr')
    this.gpxBuilder.setMetadata(
      {
        name: 'Safe Trail or Route, Croatia',
        desc: 'This is a generated GPX file',
        author: {
          name: 'SigurneStaze.hr',
          email: { '@id': 'info.sigurnestaze', '@domain': 'hgss.hr'},
          link: { '@href': 'https://sigurnestaze.hr/'}
        } as personType,
        link: [
          { '@href': 'https://sigurnestaze.hr/' } as linkType,
          { '@href': 'https://safetrails.hr/' } as linkType,
        ],
        time: moment.utc().local().toISOString(),
        keywords: 'sigurnestaze.hr,hrvatska,safetrails.hr,croatia'

      } as metadataType
    )
  }

  static getCoordinate4326(coord: OlCoordinate) {
    return transform(coord, "EPSG:3857", "EPSG:4326");
  }

  static getCoordinates4326(geom: OlLineString) {
    return geom.getCoordinates()?.map(x => transform(x, 'EPSG:3857', 'EPSG:4326'))
  }

  static trimDecimals(x: number, decimals: number = 6) {
    return parseFloat(x.toFixed(decimals));
  }

  static getWptsFromRoute(rpts: Array<RoutePoint>) {
    return rpts.map(x => {
      const coord4326 = GPXBuilderOL.getCoordinate4326(x.coord)
      const pt = {
        '@lat': GPXBuilderOL.trimDecimals(coord4326[1]),
        '@lon': GPXBuilderOL.trimDecimals(coord4326[0])
      } as wptType;
      if (x.label) {
        pt.name = x.label;
      }

      return pt;
    })
  }

  static getWpts(coords: Array<OlCoordinate>) {
    // eslint-disable-next-line arrow-body-style
    const pts = coords.map(x => {
      return {
        '@lat': GPXBuilderOL.trimDecimals(x[1]),
        '@lon': GPXBuilderOL.trimDecimals(x[0]),
        ele: GPXBuilderOL.trimDecimals(x[2], 1)
      } as wptType;
    });

    return pts;
  }

  static getFirstPoint(pts: Array<wptType>, name?: string) {
    const pt = {
      '@lat': pts[0]['@lat'],
      '@lon': pts[0]['@lon'],
      ele: pts[0].ele
    } as wptType;

    if (name) {
      pt.name = name;
    }

    return pt;
  }

  static getLastPoint(pts: Array<wptType>, name?: string) {
    const pt = {
      '@lat': pts[pts.length - 1]['@lat'],
      '@lon': pts[pts.length - 1]['@lon'],
      ele: pts[pts.length - 1].ele
    } as wptType;

    if (name) {
      pt.name = name;
    }

    return pt;
  }

  addStaza(rec: DCRecord, feat: OlFeature<OlLineString>) {

    const geom = feat.getGeometry()
    if (geom) {
      const coords4326 = GPXBuilderOL.getCoordinates4326(geom);

      const pts = GPXBuilderOL.getWpts(coords4326);

      const firstPoint = GPXBuilderOL.getFirstPoint(pts, rec.pocetak as string);
      const lastPoint = GPXBuilderOL.getLastPoint(pts, rec.kraj as string);

      this.gpxBuilder.addWaypoint(firstPoint);
      this.gpxBuilder.addWaypoint(lastPoint);

      const trk = {
        name: `${rec.hps_broj_staza ? (rec.hps_broj_staza + '. ') : ''}${rec.hps_naziv}`,
        desc: rec.hps_oznaka,
        link: rec.hps_info_url ? [{ '@href': rec.hps_info_url} as linkType] : undefined,
        trkseg: [{ trkpt: pts }] as Array<trksegType>
      } as trkType;

      this.gpxBuilder.addTrack(trk);
    }

  }

  addStazaFromRoute(rec: DCRecord, feat: OlFeature<OlLineString>, routingPoints: Array<RoutePoint>) {

    // console.log(rec, feat, routingPoints);

    const geom = feat.getGeometry()
    if (geom) {
      const coords4326 = GPXBuilderOL.getCoordinates4326(geom);

      const pts = GPXBuilderOL.getWpts(coords4326);

      // const firstPoint = GPXBuilderOL.getFirstPoint(pts, 'Start');
      // const lastPoint = GPXBuilderOL.getLastPoint(pts, 'Finish');

      const routeWpts = GPXBuilderOL.getWptsFromRoute(routingPoints);

      // this.gpxBuilder.addWaypoint(firstPoint);
      // this.gpxBuilder.addWaypoint(lastPoint);
      this.gpxBuilder.addWaypoints(routeWpts);

      const trk = {
        name: rec.hps_naziv,
        desc: rec.hps_oznaka,
        trkseg: [{ trkpt: pts }] as Array<trksegType>
      } as trkType;

      this.gpxBuilder.addTrack(trk);
    }
  }

  writeGPX() {
    return this.gpxBuilder.writeGPX();
  }
}

export default GPXBuilderOL;
